import { useEffect } from 'react'
import { Link, router } from '@inertiajs/react'
import type { MeetingStats } from '../../types'

interface AgendaTemplateSummary {
  id: number
  name: string
  total_duration_minutes: number
}

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00')
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Tomorrow'
  if (diffDays < 7) return `in ${diffDays} days`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

export default function NextMeetingCard({
  stats,
  teamId,
  agendaTemplates = [],
  canStartMeeting = false,
}: {
  stats: MeetingStats
  teamId: number
  agendaTemplates?: AgendaTemplateSummary[]
  canStartMeeting?: boolean
}) {
  const handleStartMeeting = (templateId?: number) => {
    router.post(`/teams/${teamId}/start_meeting`, templateId ? { agenda_template_id: templateId } : {})
  }

  // While the card is in its "Meeting in Progress" state, keep meeting_stats
  // fresh so a meeting that's been completed elsewhere (auto-complete after
  // the cutoff, manual end in another tab) drops out of the card without
  // requiring a manual page refresh. We poll on a quiet ~30s interval and
  // also reload immediately whenever the tab regains focus / becomes
  // visible. The effect is gated on stats.meeting_in_progress so any other
  // card state stays completely silent.
  const inProgress = !!stats.meeting_in_progress
  useEffect(() => {
    if (!inProgress) return

    const reload = () => {
      router.reload({ only: ['meeting_stats'] })
    }

    const interval = window.setInterval(reload, 30000)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') reload()
    }
    window.addEventListener('focus', reload)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', reload)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [inProgress])

  return (
    <div className="bg-white rounded-lg border border-stone">
      <div className="flex items-center justify-between px-5 py-4 border-b border-stone">
        <h3 className="text-base font-bold uppercase tracking-widest text-text-secondary">Meetings</h3>
        <Link href={`/teams/${teamId}/agendas`} className="text-sm font-semibold text-text-primary hover:underline">
          View All →
        </Link>
      </div>

      <div className="p-5">
        {stats.meeting_in_progress ? (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#15532E] opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#15532E]" />
              </span>
              <span className="text-sm font-semibold text-[#15532E]">Meeting in Progress</span>
            </div>

            {stats.meeting_in_progress.template_name && (
              <p className="text-lg font-semibold text-text-primary mb-1">
                {stats.meeting_in_progress.template_name}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-secondary mb-3">
              {stats.meeting_in_progress.started_at && (
                <span>
                  Started {new Date(stats.meeting_in_progress.started_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
              {stats.meeting_in_progress.facilitator && (
                <span>
                  · Led by {stats.meeting_in_progress.facilitator.first_name} {stats.meeting_in_progress.facilitator.last_name}
                </span>
              )}
            </div>

            {stats.meeting_in_progress.attendees && stats.meeting_in_progress.attendees.length > 0 && (
              <div className="flex items-center gap-1 mb-3">
                <div className="flex -space-x-2">
                  {/* allow-unsorted-people: do NOT alphabetize. This mirrors
                      the live meeting's attendee strip, which keeps the
                      facilitator's custom drag order. */}
                  {stats.meeting_in_progress.attendees.slice(0, 5).map((a) => (
                    <div
                      key={a.id}
                      title={`${a.first_name} ${a.last_name}`}
                      className="w-7 h-7 rounded-full bg-[#351E1D] text-white text-xs font-semibold flex items-center justify-center border-2 border-white"
                    >
                      {a.first_name[0]}{a.last_name[0]}
                    </div>
                  ))}
                  {stats.meeting_in_progress.attendees.length > 5 && (
                    <div className="w-7 h-7 rounded-full bg-[#F5F5F5] text-text-secondary text-xs font-semibold flex items-center justify-center border-2 border-white">
                      +{stats.meeting_in_progress.attendees.length - 5}
                    </div>
                  )}
                </div>
                <span className="text-xs text-text-muted ml-1">
                  {stats.meeting_in_progress.attendees.length} attendee{stats.meeting_in_progress.attendees.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}

            <Link
              href={`/meetings/${stats.meeting_in_progress.id}/join`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#15532E] text-white text-sm font-semibold rounded-lg hover:bg-[#0F3D22] transition-colors"
            >
              Join Meeting →
            </Link>
          </div>
        ) : (
          <div>
            {stats.next_meeting && (
              <div className="mb-5 pb-5 border-b border-stone/50">
                <p className="text-sm text-text-secondary mb-1">Next Meeting</p>
                <p className="text-xl font-semibold text-text-primary mb-1">
                  {formatDate(stats.next_meeting.scheduled_date)}
                  <span className="text-base font-normal text-text-secondary ml-2">
                    ({formatRelativeDate(stats.next_meeting.scheduled_date)})
                  </span>
                </p>
                {stats.next_meeting.facilitator && (
                  <p className="text-base text-text-secondary">
                    Facilitator: {stats.next_meeting.facilitator.first_name} {stats.next_meeting.facilitator.last_name}
                  </p>
                )}
              </div>
            )}

            {!stats.next_meeting && stats.last_meeting && (
              <div className="mb-5 pb-5 border-b border-stone/50">
                <p className="text-sm text-text-muted">
                  Last meeting: {formatDate(stats.last_meeting.ended_at)}
                  {(stats.last_meeting.average_rating ?? stats.last_meeting.rating) != null && (
                    <span className="ml-2">
                      · Rating: {(stats.last_meeting.average_rating ?? stats.last_meeting.rating)?.toFixed(1)}/6
                    </span>
                  )}
                </p>
              </div>
            )}

            {canStartMeeting && agendaTemplates.length > 0 ? (
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-text-secondary mb-3">
                  Available Templates
                </p>
                <div className="space-y-2">
                  {agendaTemplates.map((template) => (
                    <div
                      key={template.id}
                      className="flex items-center justify-between p-3 rounded-lg border border-stone/60 hover:border-charcoal/20 hover:bg-[#F5F5F5]/30 transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="text-base font-medium text-text-primary truncate">{template.name}</p>
                        <p className="text-sm text-text-secondary">
                          {formatDuration(template.total_duration_minutes)}
                        </p>
                      </div>
                      <button
                        onClick={() => handleStartMeeting(template.id)}
                        className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-pinnacle-red text-white text-sm font-semibold rounded-md hover:bg-pinnacle-red/90 transition-colors"
                      >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                          <polygon points="5 3 19 12 5 21 5 3" />
                        </svg>
                        Start
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : canStartMeeting && agendaTemplates.length === 0 ? (
              <div className="text-center py-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-3 text-text-muted">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <p className="text-sm text-text-secondary mb-1">No agenda templates assigned.</p>
                <p className="text-sm text-text-muted">
                  Assign a template to this team from the Templates page.
                </p>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-text-secondary">No meetings scheduled yet.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
